import { useMemo } from 'react';

import { differenceInCalendarDays } from 'date-fns';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider } from 'react-admin';
import { CancelButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { Typography } from 'shared/mui/Typography';
import { ActivePeriodField } from 'shared/react-admin/Fields/ActivePeriodField/ActivePeriodField';
import { useSyncNestedDialogState } from 'shared/widgets/CommissionsTabs/context/CommissionsNestedDialogsContext';
import { CommissionRecalculationTask } from 'types/embedded/commissionRecalculationTask';
import { parseDateInput } from 'utils/date';

type Props = {
  onClose: VoidFunction;
  record: CommissionRecalculationTask | undefined;
} & DialogProps;

export const CommissionsTabsTaskShowDialog: React.FC<Props> = ({ open, onClose, record }) => {
  const translate = useTypedTranslate();

  useSyncNestedDialogState(open);

  const days = useMemo(() => {
    if (!record) {
      return 0;
    }

    const fromDate = parseDateInput(record.recalculateFrom);
    const toDate = parseDateInput(record.recalculateTo);

    return fromDate && toDate && differenceInCalendarDays(toDate, fromDate) + 1;
  }, [record]);

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <Card>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.commissionsRecalculation.showDialog.title')}
          titleSize="smallBold"
        />
        <CardContent>
          <CardTable tableBodyProps={{ sx: { '& td': { width: '272px' } } }}>
            <TableRowWrapper label={translate('app.commissionsRecalculation.showDialog.period')}>
              <Typography>
                <RecordContextProvider value={record}>
                  <ActivePeriodField
                    endSource="recalculateTo"
                    startSource="recalculateFrom"
                    timezoneSource="timeZone"
                  />
                </RecordContextProvider>
              </Typography>
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.commissionsRecalculation.showDialog.days')}>
              <Typography>~ {days}</Typography>
            </TableRowWrapper>
            <TableRowWrapper label={translate('app.commissionsRecalculation.showDialog.amount')}>
              <Typography>~ {record?.totalOrdersCount}</Typography>
            </TableRowWrapper>
          </CardTable>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            <CancelButton
              key="cancel-button"
              label={translate('app.buttons.close')}
              onClick={onClose}
              variant="outlined"
            />,
          ]}
        />
      </Card>
    </Dialog>
  );
};
